import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TrackingWidget from '../components/TrackingWidget';
import ShippingCalculator from '../components/ShippingCalculator';

const { FiArrowRight, FiPlayCircle, FiZap, FiMap, FiShield, FiNavigation, FiX, FiGlobe, FiFileText } = FiIcons;

const Home = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#calculator-section') {
      setTimeout(() => {
        document.getElementById('calculator-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-metallic-light">
      <section className="relative min-h-[90vh] bg-navy flex items-center justify-center pt-24 pb-48 md:pb-64">
        {/* Abstract Tech Background */}
        <div className="absolute inset-0 z-0 opacity-60 overflow-hidden">
          <img 
            src="https://www.msrcosmos.com/wp-content/uploads/2023/08/TM-International-Logistics-leverages-Azure-to-streamline-business-processes-and-gain-real-time-insights.webp?e=jpg?q=80&w=2070&auto=format&fit=crop" 
            alt="Logistics Drone View" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-navy to-transparent"></div>
        </div>

        {/* Hero Content - Teks Disesuaikan */}
        <div className="max-w-[1200px] w-full mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl w-full"
          >
            <div className="mt-[35px] inline-block px-4 py-1.5 rounded-full bg-navy border border-orange/50 text-white text-sm font-semibold mb-6 flex items-center gap-2 w-fit shadow-[0_0_10px_rgba(254,161,22,0.3)]">
              <span className="w-2 h-2 rounded-full bg-orange animate-pulse"></span>
              Nusantara International Logistik
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 tracking-tight">
              Fast, Safe, and Reliable <span className="text-[#e46135] pb-2">Worldwide</span><br /> Delivery
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-lg leading-relaxed">
              We provide professional international shipping services for all types of cargo, including: General Cargo, Heavy Equipment, Agricultural Machinery, Industrial Machines, Project Cargo, Commercial Goods, and Personal & Business Shipments.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => document.getElementById('calculator-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#e46135] hover:bg-[#c54a22] text-white px-8 py-3.5 rounded-full font-bold transition-all flex items-center gap-2"
              >
                Mulai Pengiriman <SafeIcon icon={FiArrowRight} />
              </button>
              <button 
                onClick={() => setIsVideoOpen(true)}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-3.5 rounded-full font-semibold transition-all flex items-center gap-2"
              >
                <SafeIcon icon={FiPlayCircle} /> Tonton Video
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Popup Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/90 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl shadow-2xl overflow-hidden"
            >
              <button 
                onClick={() => setIsVideoOpen(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-orange text-white rounded-full flex items-center justify-center transition-colors backdrop-blur-md"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/MrCX1ruIDQY?autoplay=1" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
                className="border-0"
              ></iframe>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <section className="px-6 relative z-40 -mt-24 md:-mt-32 flex justify-center">
        <TrackingWidget />
      </section>

      {/* Calculator Section */}
      <section id="calculator-section" className="pt-28 pb-20 px-6 max-w-[1200px] mx-auto relative z-10 scroll-mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">Estimasi Cerdas dengan Kalkulator AI</h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Dapatkan tarif spot real-time dan pencocokan armada otomatis. Sistem kami menghitung dimensi, rute terbaik, dan jenis armada untuk efisiensi biaya maksimal.
            </p>
            
            <div className="space-y-4">
              {[
                { icon: FiZap, title: 'Tarif Terbuka', desc: 'Harga transparan tanpa biaya tersembunyi.' },
                { icon: FiMap, title: 'Jangkauan Luas', desc: 'Pengiriman mencakup seluruh wilayah Nusantara dan Internasional.' },
                { icon: FiShield, title: 'Aman & Terlindungi', desc: 'Penanganan kargo profesional dengan opsi asuransi.' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-white transition-colors border border-transparent hover:border-slate-200">
                  <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center shrink-0">
                    <SafeIcon icon={item.icon} className="text-orange text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy text-lg">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100"
          >
            <h3 className="text-xl font-bold text-navy mb-6 border-b border-slate-100 pb-4">Kalkulator Pengiriman Ringkas</h3>
            <ShippingCalculator />
          </motion.div>
        </div>
      </section>

      {/* List Layanan Utama di Homepage */}
      <section className="bg-navy py-24 text-white relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Layanan Pengiriman Terintegrasi</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">Solusi end-to-end untuk semua kebutuhan logistik Anda, menjamin keamanan dan ketepatan waktu pengiriman hingga ke tujuan akhir.</p>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { 
              icon: FiNavigation, 
              title: 'Air Freight', 
              desc: 'Layanan kargo udara pengiriman domestik dan internasional dengan jadwal fleksibel.' 
            },
            { 
              icon: FiGlobe, 
              title: 'Sea Freight', 
              desc: 'Solusi pengiriman kargo laut yang efisien dan andal dengan jangkauan global.' 
            },
            { 
              icon: FiMap, 
              title: 'Door to Door Delivery', 
              desc: 'Pengiriman langsung dari lokasi Anda hingga ke alamat tujuan dengan aman.' 
            },
            { 
              icon: FiShield, 
              title: 'Customs Clearance', 
              desc: 'Penanganan dokumen kepabeanan ekspor dan impor secara cepat dan tepat.' 
            },
            { 
              icon: FiZap, 
              title: 'Export Import Handling', 
              desc: 'Solusi penanganan ekspor dan impor terpadu standar internasional.' 
            },
            { 
              icon: FiFileText, 
              title: 'Packaging & Documentation', 
              desc: 'Dukungan penuh untuk pengepakan dan kelengkapan dokumen pengiriman Anda.' 
            }
          ].map((feat, i) => (
            <motion.div 
              key={i}
              whileHover={{ y: -10 }}
              className="glass-panel-dark p-8 rounded-2xl"
            >
              <div className="w-14 h-14 bg-orange/20 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon icon={feat.icon} className="text-orange text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{feat.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{feat.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;